import {policy} from './policy.js';
import {t} from './i18n.js';
import {recordings} from './audio-recordings.js';
import {AUDIO_CACHE,prefetchAudio} from './audio-cache.js';
import {timestampFor,sentenceStarts} from './audio-position.js';
import {loadRecordingTimings} from './audio-timings.js';

export const parseCues=source=>policy('cues',source);
export class AudioReader {
  constructor(engine,emit,persist){
    this.engine=engine;this.emit=emit;this.persist=persist;this.audio=new Audio();
    this.audio.preload='metadata';this.rate=1;this.voice='robo';this.active=false;
    this.cues=[];this.blocks=[];this.epoch=0;this.speaking=null;this.audioChapter=0;this.timingLoads=new Map();this.timingData=new Map();this.wordRequest=0;
    this.audio.addEventListener('timeupdate',()=>this.tick());
    this.audio.addEventListener('loadedmetadata',()=>{
      if(this.pendingPosition!=null){this.audio.currentTime=Math.min(this.audio.duration||this.pendingPosition,this.pendingPosition);this.pendingPosition=null;}
      this.applyReadingStart();this.audio.playbackRate=this.rate;this.state();
    });
    this.audio.addEventListener('play',()=>{this.active=true;this.mediaSession();this.state();this.prefetchChapters();});
    this.audio.addEventListener('ended',()=>{this.save().catch(()=>{});this.state();this.emit('audioEnded',{bookId:this.engine.book?.id,chapter:this.audioChapter,available:this.engine.book?.chapters.map((_,index)=>index).filter(index=>this.chapterSource(index))||[]});});
    for(const event of ['pause','ratechange'])this.audio.addEventListener(event,()=>this.state());
    this.audio.addEventListener('error',()=>{if(this.usesFile()&&this.active){if(!navigator.onLine)this.offlineUnavailable().catch(console.warn);else emit('error',t("ui-this-audio-file-could-not-be-played-try-downloading"));}});
  }
  recording(){return this.engine.book?.id==='easypeasy-en'?recordings[this.voice]:null;}
  chapterSource(index=this.audioChapter){const recording=this.recording(),chapter=this.engine.book?.chapters?.[index];return recording&&chapter&&!recording.unavailableChapters?.includes(chapter.id)?`${recording.directory}/${encodeURIComponent(chapter.id)}.mp3`:null;}
  clipKey(){return this.voice+':'+(this.engine.book?.chapters?.[this.audioChapter]?.id||'');}
  attach(saved){
    this.chapterRequest=(this.chapterRequest||0)+1;this.seekRequest=(this.seekRequest||0)+1;
    this.stop();this.lastHeard=null;this.navigationBlocks=null;this.timingRevision=(this.timingRevision||0)+1;this.readingStart=null;this.saved=saved||{};this.bookId=this.engine.book.id;
    this.cues=saved?.cues||[];
    if(this.objectUrl)URL.revokeObjectURL(this.objectUrl);
    this.objectUrl=saved?.blob?URL.createObjectURL(saved.blob):null;
    this.emit('audioLoaded',{bookId:this.engine.book.id,chapter:this.engine.capture()?.chapter||0,chapterCount:this.engine.book.chapters?.length||1,saved:{...this.saved,blob:undefined,hasFile:!!saved?.blob}});
    this.manifestPromise ||= fetch(new URL('audio/recordings.json',document.baseURI)).then(response=>{if(!response.ok)throw Error(t("ui-audio-manifest-unavailable"));return response.json();}).catch(()=>null);
    this.manifestPromise.then(manifest=>{this.manifest=manifest;this.state();});
  }
  configure({bookId,voice,chapter,rate,progress}){
    if(bookId!==this.engine.book?.id)return;
    this.voice=voice;this.audioChapter=chapter;this.rate=rate;this.progress=progress;
    this.configureFile();this.state();this.ensureTimings().catch(()=>{});
  }
  configureFile(){
    this.readingStart=null;this.offlineFailure=null;
    const chapterSrc=this.chapterSource(),key=this.clipKey();
    const src=this.voice==='attached'?this.objectUrl:chapterSrc?new URL(chapterSrc,document.baseURI).href:null;
    this.pendingPosition=this.voice==='attached'?this.saved.position||0:this.saved.recordingFiles?.[key]===chapterSrc?this.saved.recordings?.[key]||0:0;
    if(src)this.audio.src=src;else this.audio.removeAttribute('src');
    this.audio.load();this.audio.playbackRate=this.rate;
  }
  usesFile(){return !!this.audio.src&&(this.voice==='attached'||!!this.recording());}
  canPlay(){return this.voice==='robo'||this.usesFile();}
  wholeRecording(){
    const metadata=this.recording()&&this.manifest?.recordings?.[this.voice];
    if(!metadata||metadata.chapters.length!==this.engine.book.chapters.length||!metadata.chapters.every((chapter,index)=>chapter.id===this.engine.book.chapters[index].id))return null;
    return metadata;
  }
  state(){
    const file=this.usesFile(),robo=this.voice==='robo';
    const playing=this.canPlay()&&(file?!this.audio.paused:this.playing===true);
    const position=file?this.audio.currentTime:robo?this.speechPosition||0:0;
    const duration=file?(Number.isFinite(this.audio.duration)?this.audio.duration:0):robo?this.speechDuration||0:0;
    this.reportNavigationMap();const whole=this.wholeRecording();
    const totalDuration=whole?.duration||this.recording()?.runtime||duration;
    const totalPosition=position;
    const speechIndex=Math.min(this.speechIndex??0,this.blocks.length-1);
    const current=this.voice==='robo'&&this.blocks.length?{kind:'speech',index:speechIndex,offset:(this.speechIndex??0)>=this.blocks.length?this.blocks[speechIndex].textContent.length:this.spokenOffset??this.speechOffset??0}:this.recording()?{kind:'recording',chapter:this.audioChapter,seconds:this.audio.currentTime}:null;
    this.emit('audio',{hasProgress:!!this.progress,navigation:{timeline:whole?.chapters||[],recording:!!this.recording(),current},totalPosition,totalSeekable:this.recording()?!!whole:duration>0,totalDuration,playing,position,duration,rate:this.rate,active:this.active,voice:this.voice,available:this.canPlay(),placeholder:this.recording()?.placeholder===true,download:this.chapterSource()||'',chapter:this.audioChapter,chapterCount:this.recording()?this.engine.book.chapters.length:0,chapterTitle:this.recording()?this.engine.book.chapters[this.audioChapter]?.title||'':''});
    if('mediaSession'in navigator){
      navigator.mediaSession.playbackState=this.active?(playing?'playing':'paused'):'none';
      if(file&&duration>0)try{navigator.mediaSession.setPositionState?.({duration,position:Math.min(duration,Math.max(0,position)),playbackRate:this.rate});}catch{}
    }
  }
  recordingTimings(index=this.audioChapter){
    const recording=this.recording();if(!recording)return [];
    const chapter=this.engine.book.chapters[index],loaded=this.timingData.get(this.voice+':'+chapter?.id);
    return recording.timings?.[chapter?.id]||(loaded?.chapter===chapter?loaded.timings:[]);
  }
  async ensureTimings(index=this.audioChapter){
    const recording=this.recording(),chapter=this.engine.book?.chapters?.[index],voice=this.voice;
    if(recording?.timings?.[chapter?.id])return;
    if(!recording?.timingDirectory||!chapter||!this.chapterSource(index))return;
    const timings=await loadRecordingTimings(recording,chapter,document.baseURI,this.timingLoads);
    this.timingData.set(voice+':'+chapter.id,{chapter,timings});this.timingRevision=(this.timingRevision||0)+1;
    if(this.voice===voice&&this.engine.book.chapters[index]===chapter)this.state();
  }
  reportNavigationMap(){
    const blocks=this.voice==='robo'?this.blocks:this.navigationBlocks||(this.emptyNavigationBlocks??=[]);
    if(this.mapBlocks===blocks&&this.mapVoice===this.voice&&this.mapRevision===this.timingRevision&&this.mapBook===this.engine.book)return;
    this.mapBlocks=blocks;this.mapVoice=this.voice;this.mapRevision=this.timingRevision;this.mapBook=this.engine.book;
    this.emit('audioMap',{
      blocks:blocks.map(block=>({id:block.id,chapter:Number(block.closest('.book-chapter')?.dataset.index||0),starts:sentenceStarts(block.textContent,this.engine.book.language)})),
      timings:(this.engine.book?.chapters||[]).flatMap((_,chapter)=>{const entries=this.recordingTimings(chapter);return entries.length?[{chapter,entries}]:[];})
    });
  }
  async navigateTo(target){
    if(!target||(target.kind==='speech')!==(this.voice==='robo'))return;
    if(target.kind==='speech'){target={...target,block:this.blocks[target.index]};if(!target.block)return;}
    this.progress=true;this.saved.started=true;
    if(this.voice==='robo'){
      const playing=this.playing===true;this.epoch++;speechSynthesis.cancel();this.speaking?.classList.remove('speaking');this.speaking=null;
      this.speechIndex=target.index;this.speechOffset=target.offset;this.spokenOffset=target.offset;
      this.speechPosition=this.durations.slice(0,target.index).reduce((sum,duration)=>sum+duration,0)+this.durations[target.index]*target.offset/Math.max(1,target.block.textContent.length);
      if(playing){speechSynthesis.resume();this.speak(this.epoch);}else this.state();
    }else{
      const playing=!this.audio.paused;
      if(target.chapter!==this.audioChapter){await this.selectChapter(target.chapter,false,true,true);this.readingStart={seconds:target.seconds};this.applyReadingStart();if(playing)await this.playFile();}
      else await this.seek(target.seconds,{keepReturn:true});
      this.state();
    }
    await this.save();
  }
  async selectVoice(voice){
    if(!['hackauthor','hackauthor-no-breaks','denshi','ai-girlfriend','robo'].includes(voice)||voice===this.voice)return;
    this.chapterRequest=(this.chapterRequest||0)+1;this.seekRequest=(this.seekRequest||0)+1;
    await this.save();this.stop();this.voice=voice;this.saved={...this.saved,voice,audioChapters:{...this.saved.audioChapters,[voice]:this.audioChapter}};
    this.configureFile();await this.prepare();await this.save();
  }
  async selectChapter(index,play=this.usesFile()&&!this.audio.paused,follow=true,preserveNavigation=false){
    if(!this.recording()||index<0||index>=this.engine.book.chapters.length)return;
    const request=this.chapterRequest=(this.chapterRequest||0)+1;
    this.progress=true;this.saved.started=true;const active=this.active;await this.save();
    if(request!==this.chapterRequest)return;
    this.stop(preserveNavigation);this.audioChapter=index;this.saved={...this.saved,audioChapters:{...this.saved.audioChapters,[this.voice]:index}};this.configureFile();this.active=active;
    if(follow&&this.engine.settings.follow)this.engine.chapter(index);
    await this.ensureTimings().catch(()=>{});
    if(request!==this.chapterRequest)return;
    this.state();if(play){this.active=true;await this.playFile();}await this.save();
  }
  prefetchChapters(){
    if(!this.recording()||!navigator.serviceWorker?.controller)return;
    const key=this.clipKey();if(this.prefetchKey===key)return;
    this.prefetchKey=key;this.prefetchController?.abort();this.prefetchController=new AbortController();
    const available=this.engine.book.chapters.map((_,index)=>index).filter(index=>this.chapterSource(index));
    this.emit('audioPrefetch',{available,chapter:this.audioChapter});
  }
  prefetch({next,keep}){
    if(!this.recording())return;
    const urls=indexes=>indexes.map(index=>this.chapterSource(index)).filter(Boolean).map(source=>new URL(source,document.baseURI).href);
    this.prefetchController?.abort();this.prefetchController=new AbortController();
    prefetchAudio(urls(next),urls(keep),this.prefetchController.signal).catch(()=>{});
  }
  async prepareSpeech(target=this.engine.listeningTarget()){
    this.blocks=this.engine.speakingBlocks();this.speechIndex=Math.max(0,this.blocks.indexOf(target?.block));this.speechOffset=target?.offset||0;this.spokenOffset=this.speechOffset;
    const epoch=this.epoch,blocks=this.blocks;
    const plan=await policy('speech',{texts:blocks.map(block=>block.textContent),index:this.speechIndex,offset:this.speechOffset});
    if(epoch!==this.epoch||blocks!==this.blocks)return;
    this.durations=plan.durations;this.speechDuration=plan.duration;this.speechPosition=plan.position;
  }
  async prepare(fromViewport=false){
    const target=fromViewport&&!this.progress?this.engine.listeningTarget():null,epoch=this.epoch;
    await this.ensureTimings(target?.chapter??this.audioChapter).catch(()=>{});
    if(epoch!==this.epoch)return;
    if(this.recording())this.navigationBlocks ||= this.engine.speakingBlocks();
    if(target&&!this.progress)await this.startFromReading(target);
    else if(this.voice==='robo'&&!this.blocks.length)await this.prepareSpeech();
    this.state();
  }
  async startFromReading(target){
    if(!target)return false;
    const epoch=this.epoch,book=this.engine.book;
    if(this.voice==='robo'){await this.prepareSpeech(target);return epoch===this.epoch&&book===this.engine.book;}
    if(this.recording()){
      const seconds=await timestampFor(target,this.recordingTimings(target.chapter));
      if(epoch!==this.epoch||book!==this.engine.book)return false;
      if(this.audioChapter!==target.chapter){this.audioChapter=target.chapter;this.configureFile();}
      this.readingStart={fraction:target.fraction,seconds:seconds??(this.recording().timingDirectory?0:undefined)};
    }else if(this.voice==='attached'){
      const cue=this.cues.find(c=>{try{return JSON.parse(c.location).block===target.blockId;}catch{return c.location===target.blockId;}});
      if(!cue){this.emit('toast',t('ui-add-audiobook-timestamps-to-sync-this-recording-with-the'));return false;}
      this.readingStart={seconds:cue.start};
    }
    this.pendingPosition=null;this.applyReadingStart();this.state();return true;
  }
  applyReadingStart(){
    if(!this.readingStart||!Number.isFinite(this.audio.duration))return;
    const {seconds,fraction}=this.readingStart;
    this.audio.currentTime=Math.min(Math.max(0,seconds??fraction*this.audio.duration),Math.max(0,this.audio.duration-.25));
    this.readingStart=null;
  }
  async startAtWord(target){
    const epoch=this.epoch,request=++this.wordRequest;
    if(this.recording()?.timingDirectory){
      if(!this.chapterSource(target.chapter)){this.emit('toast',t("ui-this-narrator-does-not-read-this-chapter-choose-the"));return;}
      await this.ensureTimings(target.chapter);
      if(epoch!==this.epoch||request!==this.wordRequest)return;
      const timestamp=await timestampFor(target,this.recordingTimings(target.chapter));
      if(epoch!==this.epoch||request!==this.wordRequest)return;
      if(timestamp===undefined){this.emit('toast',t("ui-this-passage-is-not-matched-to-the-recording-choose"));return;}
    }
    if(epoch!==this.epoch||request!==this.wordRequest)return;
    this.stop();if(!await this.startFromReading(target))return;await this.toggle();
    if(this.recording()&&!this.recording()?.timingDirectory&&await timestampFor(target,this.recordingTimings(target.chapter))===undefined)this.emit('toast',t("ui-starting-near-this-word-recording-timestamps-are-not-available"));
  }
  async savedClips(){
    const book=this.engine.book,clips=[];
    if(!book)return clips;
    if(this.objectUrl)clips.push({voice:'attached',chapter:0,title:this.saved.name||book.title,narrator:t('ui-your-attached-audiobook')});
    if(book.id!=='easypeasy-en'||!('caches'in window))return clips;
    const cached=new Set((await(await caches.open(AUDIO_CACHE)).keys()).map(request=>request.url));
    for(const [voice,recording]of Object.entries(recordings))for(const [chapter,item]of book.chapters.entries()){
      const url=new URL(`${recording.directory}/${encodeURIComponent(item.id)}.mp3`,document.baseURI).href;
      if(cached.has(url))clips.push({voice,chapter,title:item.title,narrator:recording.name});
    }
    return clips;
  }
  listeningSpot(){
    const book=this.engine.book,heard=this.lastHeard||this.saved?.lastHeard;
    if(!heard||heard.bookId!==book?.id||heard.version!==book.version||typeof heard.exact!=='string'||!heard.exact||!Number.isInteger(heard.textOffset)||heard.textOffset<0)return null;
    const chapter=book.chapters?.find(item=>item.id===heard.chapterId);
    if(!chapter)return null;
    // Verify the recorded block still exists before handing its UTF-16 anchor
    // back to the reader. Never infer a text offset from clip duration.
    const holder=document.createElement('template');holder.innerHTML=chapter.html;
    const block=[...holder.content.querySelectorAll('[id]')].find(node=>node.id===heard.block);
    if(!block||block.textContent.slice(heard.textOffset,heard.textOffset+heard.exact.length)!==heard.exact)return null;
    return {kind:'flow',chapterId:heard.chapterId,block:heard.block,textOffset:heard.textOffset,textY:140};
  }
  rememberHeard(cue){
    if(!cue||!this.recording()||cue.chapter!==this.audioChapter||!this.audio.currentTime)return;
    const chapter=this.engine.book.chapters[this.audioChapter];
    if(this.heardChapter!==chapter){this.heardChapter=chapter;this.heardMarkup=document.createElement('template');this.heardMarkup.innerHTML=chapter.html;}
    const holder=this.heardMarkup;
    const block=[...holder.content.querySelectorAll('[id]')].find(node=>node.id===cue.block),offset=cue.offset||0;
    if(!block||offset>=block.textContent.length)return;
    this.lastHeard={bookId:this.engine.book.id,version:this.engine.book.version,chapterId:chapter.id,block:cue.block,textOffset:offset,exact:block.textContent.slice(offset,offset+60)};
  }
  async showSavedAudio(unavailable=false){
    const bookId=this.engine.book?.id,clips=await this.savedClips();
    if(bookId!==this.engine.book?.id)return;
    this.emit('savedAudio',{bookId,clips,unavailable,listeningSpot:!!this.listeningSpot()});
  }
  offlineUnavailable(){
    if(!this.offlineFailure)this.offlineFailure=(async()=>{this.audio.pause();this.state();await this.showSavedAudio(true);})();
    return this.offlineFailure;
  }
  async playFile(){
    if(this.recording())this.navigationBlocks ||= this.engine.speakingBlocks();
    this.offlineFailure=null;
    const epoch=this.epoch,src=this.audio.src;
    if(!navigator.onLine&&this.recording()&&this.audio.readyState<2){
      const cached='caches'in window&&await(await caches.open(AUDIO_CACHE)).match(this.audio.src);
      if(epoch!==this.epoch||src!==this.audio.src)return;
      if(!cached){await this.offlineUnavailable();return;}
    }
    if(this.audio.error)this.audio.load();
    try{await this.audio.play();}catch(cause){if(epoch!==this.epoch||src!==this.audio.src)return;if(navigator.onLine)throw cause;await this.offlineUnavailable();}
  }
  async replaySaved(clip){
    if(!(await this.savedClips()).some(item=>item.voice===clip.voice&&item.chapter===clip.chapter)){await this.showSavedAudio(true);return;}
    await this.save();this.stop();this.voice=clip.voice;this.audioChapter=clip.chapter;this.progress=true;this.saved.started=true;
    this.configureFile();this.pendingPosition=0;this.active=true;
    const epoch=this.epoch;await this.ensureTimings().catch(()=>{});if(epoch!==this.epoch)return;await this.playFile();await this.save();
  }
  async continueReading(){
    const book=this.engine.book,spot=this.listeningSpot();this.audio.pause();
    if(this.playing&&'speechSynthesis'in window){speechSynthesis.pause();this.playing=false;}
    this.state();await this.save();if(spot&&this.engine.book===book)await this.engine.go(spot);
  }
  async toggleInline(fresh){
    if(fresh&&!this.progress){
      const target=this.engine.inlineAudioTarget(),epoch=this.epoch;
      await this.ensureTimings(target?.chapter??this.audioChapter).catch(()=>{});
      if(epoch!==this.epoch)return;
      if(!this.progress&&!await this.startFromReading(target))return;
    }
    return this.toggle();
  }
  async toggle(){
    if(!this.canPlay())return;
    this.active=true;this.progress=true;this.saved.started=true;
    if(this.usesFile()){
      if(this.audio.paused)await this.playFile();else this.audio.pause();
      this.state();return;
    }
    if(!('speechSynthesis'in window))throw Error(t("ui-this-browser-does-not-support-spoken-text-attach-an"));
    if(this.playing){speechSynthesis.pause();this.playing=false;this.state();return;}
    if(speechSynthesis.paused&&(speechSynthesis.speaking||speechSynthesis.pending)&&this.speaking&&this.blocks.length){speechSynthesis.resume();this.playing=true;this.state();return;}
    await this.startSpeech();
  }
  async startSpeech(){const epoch=this.epoch;if(!this.blocks.length)await this.prepareSpeech();if(epoch!==this.epoch)return;if(!this.blocks.length)throw Error(t("ui-there-is-no-readable-text-here-yet-for-a"));this.playing=true;this.epoch++;speechSynthesis.resume();this.speak(this.epoch);}
  async speak(epoch){
    if(epoch!==this.epoch||!this.playing)return;
    if(this.speechIndex>=this.blocks.length){this.playing=false;this.state();return;}
    const block=this.blocks[this.speechIndex],offset=this.speechOffset||0;
    const language=this.engine.book.language||'en',voices=speechSynthesis.getVoices();
    let selected;
    try{selected=await policy('speechVoice',{language,voices:voices.map(voice=>voice.lang)});}
    catch(error){if(epoch===this.epoch){this.playing=false;this.emit('error',error.message);this.state();}return;}
    if(epoch!==this.epoch||!this.playing||block!==this.blocks[this.speechIndex])return;
    const utterance=new SpeechSynthesisUtterance(block.textContent.slice(offset));
    utterance.lang=language;utterance.rate=this.rate;utterance.voice=selected===null?null:voices[selected];
    this.speaking=block;this.spokenOffset=offset;block.classList.add('speaking');
    this.speechPosition=this.durations.slice(0,this.speechIndex).reduce((a,b)=>a+b,0)+offset/Math.max(1,block.textContent.length)*this.durations[this.speechIndex];
    if(this.engine.settings.follow)this.engine.go({kind:'flow',block:block.id,chapterId:block.closest('.book-chapter')?.id});
    utterance.onboundary=e=>{if(epoch!==this.epoch)return;this.spokenOffset=offset+e.charIndex;this.speechPosition=this.durations.slice(0,this.speechIndex).reduce((a,b)=>a+b,0)+(offset+e.charIndex)/Math.max(1,block.textContent.length)*this.durations[this.speechIndex];this.state();};
    utterance.onend=()=>{block.classList.remove('speaking');if(epoch!==this.epoch)return;this.speechIndex++;this.speechOffset=0;this.speak(epoch);};
    utterance.onerror=e=>{if(['canceled','interrupted'].includes(e.error))return;this.playing=false;if(!navigator.onLine)this.offlineUnavailable().catch(console.warn);else this.emit('error',t("ui-spoken-text-is-unavailable-with-this-devices-current-voices"));this.state();};
    speechSynthesis.speak(utterance);this.state();this.mediaSession();
  }
  stop(preserveNavigation=false){
    if(!preserveNavigation)this.emit('audioReset');
    this.epoch++;this.prefetchController?.abort();this.prefetchKey=null;this.audio.pause();if('speechSynthesis'in window)speechSynthesis.cancel();
    this.playing=false;this.active=false;this.speaking?.classList.remove('speaking');
    this.blocks=[];this.speechOffset=0;this.spokenOffset=0;this.readingStart=null;this.durations=[];this.speechPosition=0;this.speechDuration=0;this.speaking=null;
    clearTimeout(this.saveTimer);this.saveTimer=null;this.state();
  }
  async setRate(rate){
    this.rate=Math.max(.5,Math.min(3,Number(rate)||1));this.audio.playbackRate=this.rate;
    if(this.playing&&!this.usesFile()){this.epoch++;speechSynthesis.cancel();this.speak(this.epoch);}
    this.state();await this.save();
  }
  async seekRecording(target){
    if(!this.recording()||target.kind!=='recording')return;
    if(!Number.isInteger(target.chapter)||target.chapter<0||target.chapter>=this.engine.book.chapters.length||!Number.isFinite(target.seconds)||target.seconds<0)return;
    const request=this.seekRequest=(this.seekRequest||0)+1;
    this.chapterRequest=(this.chapterRequest||0)+1;
    this.emit('audioReset');const playing=!this.audio.paused;this.active=true;
    if(target.chapter!==this.audioChapter){
      await this.selectChapter(target.chapter,false,false);
      if(request!==this.seekRequest||this.audioChapter!==target.chapter)return;
      if(this.audio.readyState>0)await this.seek(target.seconds);
      else{this.pendingPosition=target.seconds;this.state();}
      if(playing)await this.playFile();await this.save();
    }else await this.seek(target.seconds);
  }
  async seek(seconds,{keepReturn=false}={}){
    if(!keepReturn)this.emit('audioReset');
    this.progress=true;this.saved.started=true;this.readingStart=null;this.pendingPosition=null;
    seconds=Math.max(0,Number(seconds)||0);
    if(this.usesFile()){
      this.audio.currentTime=Math.min(Number.isFinite(this.audio.duration)?this.audio.duration:seconds,seconds);
      await this.save();
    }else if(this.voice==='robo'){
      const request=this.speechSeekRequest=(this.speechSeekRequest||0)+1,startingEpoch=this.epoch;
      if(!this.blocks.length)await this.prepareSpeech();
      if(request!==this.speechSeekRequest||startingEpoch!==this.epoch)return;
      if(this.blocks.length){
        const epoch=this.epoch,blocks=this.blocks;
        const target=await policy('speechSeek',{durations:this.durations,seconds});
        if(epoch!==this.epoch||blocks!==this.blocks||request!==this.speechSeekRequest)return;
        this.speechIndex=target.index;this.speechOffset=0;this.spokenOffset=0;this.speechPosition=target.position;this.epoch++;
        speechSynthesis.cancel();this.speaking?.classList.remove('speaking');this.speaking=null;if(this.playing){speechSynthesis.resume();this.speak(this.epoch);}
      }
    }
    this.state();
  }
  tick(){
    if(!this.usesFile())return;
    if(!this.audio.paused)this.prefetchChapters();
    const cue=this.voice==='attached'?this.cues.find(c=>c.start<=this.audio.currentTime&&c.end>this.audio.currentTime):null;
    if(cue&&cue!==this.lastCue){this.lastCue=cue;if(this.engine.settings.follow&&cue.location){let location;try{location=JSON.parse(cue.location);}catch{location={kind:'flow',block:cue.location};}this.engine.go(location);}}
    this.state();
    // Save steadily during background playback rather than waiting for a gap in timeupdate events.
    if(!this.saveTimer)this.saveTimer=setTimeout(()=>{this.saveTimer=null;this.save().catch(error=>this.emit('error',error.message));},1000);
  }
  async save(){
    if(!this.saved||this.bookId!==this.engine.book?.id)return;
    const published=this.recording()&&this.usesFile()&&this.audio.readyState>0;
    this.saved={...this.saved,...(this.lastHeard?{lastHeard:this.lastHeard}:{}),position:this.voice==='attached'&&this.usesFile()?this.audio.currentTime:this.saved.position||0,rate:this.rate,voice:this.voice,
      ...(published?{recordings:{...this.saved.recordings,[this.clipKey()]:this.audio.currentTime},recordingFiles:{...this.saved.recordingFiles,[this.clipKey()]:this.chapterSource()},audioChapters:{...this.saved.audioChapters,[this.voice]:this.audioChapter}}:{})};
    await this.persist(this.saved,this.bookId);
  }
  mediaSession(){
    if(!('mediaSession'in navigator))return;
    navigator.mediaSession.metadata=new MediaMetadata({title:this.engine.book.title,artist:this.recording()?.name||this.engine.book.author||''});
    navigator.mediaSession.setActionHandler('play',()=>{if(this.usesFile()&&this.audio.paused||!this.usesFile()&&!this.playing)this.toggle().catch(error=>this.emit('error',error.message));});
    navigator.mediaSession.setActionHandler('pause',()=>{if(this.usesFile()&&!this.audio.paused||!this.usesFile()&&this.playing)this.toggle().catch(error=>this.emit('error',error.message));});
    navigator.mediaSession.setActionHandler('seekforward',()=>this.emit('audioNavigate','forward'));
    navigator.mediaSession.setActionHandler('seekbackward',()=>this.emit('audioNavigate','previousSentence'));
    navigator.mediaSession.setActionHandler('seekto',event=>this.emit('audioSeek',event.seekTime));
  }
}
